import React, { FC, useEffect, useRef, useState } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { Box, Chip, Grid, IconButton, makeStyles, SvgIcon, Typography } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Event, Schedule } from '@material-ui/icons';
import { ResizeObserver } from 'resize-observer';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import moment from 'moment-timezone';
import { ReactComponent as Times } from 'img/times.svg';
import { ReactComponent as Repeat } from 'img/repeat.svg';
import { ReactComponent as Building } from 'img/icons/sidebar/building.svg';
import { ReactComponent as Package } from 'img/box.svg';
import { ReactComponent as TagsIcon } from 'img/icons/tasks-deprecated.svg';
import { ReactComponent as AlignLeftIcon } from 'img/icons/align-left.svg';
import { ReactComponent as UserIcon } from 'img/icons/user_deprecated.svg';
import { ReactComponent as ServicesIcon } from 'img/icons/boxes.svg';
import { ReactComponent as SlotsIcon } from 'img/icons/slots.svg';
import {
  CancelEventModal,
  DeleteEventModal,
  DuplicateParticipantsModal,
  RemindParticipantsModal,
} from 'modules/booking/components';
import ScrollBox from 'common/components/ScrollBox/ScrollBox';
import ParticipantsList from './ParticipantsList/ParticipantsList';
import ModificationHistory from './ModificationHistory/ModificationHistory';
import { ActionsMenu, TooltipTypography } from 'common/components';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import {
  EventAttendeeType,
  EventOverbookingType,
  EventRepeatFrequency,
  IBookingEvent,
  IBookingEventResource,
  IEventAction,
  IEventPersonEntity,
  IRemindAppointmentDto,
  PersonAppointmentStatus,
} from 'modules/booking/interfaces';
import commonMessages from 'common/messages/messages';
import inputLabels from 'common/messages/inputLabels';
import messages from 'modules/booking/messages';
import { getWeekdaysLabel } from 'common/utils/labelComposer';
import { PeakModules } from 'common/constants/peakModules';
import { SenderAvailabilityTypeList } from 'modules/booking/constants/senderAvailability';
import { EShortNotificationType } from 'modules/booking/constants/notificationType';
import { transformMinutesToTimeFormat } from 'common/utils/time';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import ParticipationModal, {
  IFormParticipateEvent,
} from 'common/components/PersonProfile/components/Appointments/ParticipationModal/ParticipationModal';
import { selectCurrentUserId } from 'modules/authentication/state/selectors';
import { getArray } from 'common/utils/typeUtils';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import BookingActionItemEdit from 'components/Booking/BookingActionItemEdit';
import BookingActionItemDelete from 'components/Booking/BookingActionItemDelete';
import BookingActionItemRemind from 'components/Booking/BookingActionItemRemind';
import BookingActionItemRestore from 'components/Booking/BookingActionItemRestore';
import BookingActionItemCancel from 'components/Booking/BookingActionItemCancel';
import BookingActionItemConfirmParticipation from 'components/Booking/BookingActionItemConfirmParticipation';
import BookingActionItemCancelParticipation from 'components/Booking/BookingActionItemCancelParticipation';
import BookingActionItemDuplicate from 'components/Booking/BookingActionItemDuplicate';
import BookingActionItemJoin from 'components/Booking/BookingActionItemJoin';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingBottom: theme.spacing(1),
  },
  colorLine: {
    height: '4px',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.primary.main,
  },
  titleWrapper: {
    padding: theme.spacing(2),
  },
  actionsWrapper: {
    width: 'auto',
    '& > div:first-child': {
      marginRight: theme.spacing(0.5),
    },
  },
  scrollbarWrapper: {
    flex: 1,
    minHeight: 0,
  },
  detailsWrapper: {
    padding: theme.spacing(1, 2),
  },
  closeBtn: {
    width: '14px',
    height: '14px',
  },
  closeBtnIcon: {
    fontSize: '14px',
    opacity: '0.3',
  },
  title: {
    lineHeight: '21px',
    letterSpacing: 0,
  },
  row: {
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
  },
  rowIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.1rem',
    width: '18px',
    color: theme.palette.text.primary,
    opacity: '0.4',
  },
  tag: {
    margin: theme.spacing(0.5, 1, 0.5, 0),
    padding: theme.spacing(0.25, 1),
    height: 'auto',
    backgroundColor: theme.palette.darkBackground?.light,
    color: theme.palette.background.paper,
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 700,
  },
  inactive: {
    opacity: 0.5,
  },
  canceledStatus: {
    textTransform: 'uppercase',
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    lineHeight: '20px',
    color: theme.palette.error.main,
  },
  participantsRow: {
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
  },
  personsStatusInfo: {
    marginLeft: '26px',
    marginBottom: '12px',
  },
}));

interface IEventDetailsProps {
  personId?: number;
  event?: IBookingEvent | null;
  onClose?: () => void;
  onEdit?: (event: IBookingEvent) => void;
  onCancelParticipation?: (formData: IFormParticipateEvent) => void;
  onConfirmParticipation?: (formData: IFormParticipateEvent) => void;
  onDelete?: (formValues: IEventAction) => void;
  onCancel?: (formValues: IEventAction) => void;
  onRestore?: (event: IBookingEvent) => void;
  onDuplicate?: (isDuplicateParticipants: boolean) => void;
  onRemind?: (formValues: IRemindAppointmentDto) => void;
  handleOverflow?: (value) => void;
  appointmentStatus?: PersonAppointmentStatus;
  readOnly?: boolean;
  module?: PeakModules;
  isEventActionLoading: boolean;
  type: SenderAvailabilityTypeList;
  onLoadSenderAvailability?: (
    type: SenderAvailabilityTypeList,
    shortTypeEvent: EShortNotificationType,
  ) => void;
  onJoinClass?: (formData: IFormParticipateEvent) => void;
}

const EventDetails: FC<IEventDetailsProps> = ({
  personId,
  event,
  onEdit,
  onClose,
  onDelete,
  onCancel,
  onRestore,
  onDuplicate,
  onRemind,
  handleOverflow,
  readOnly,
  module,
  appointmentStatus,
  isEventActionLoading,
  type,
  onCancelParticipation,
  onConfirmParticipation,
  onLoadSenderAvailability,
  onJoinClass,
}) => {
  const userId = useRootSelector(selectCurrentUserId);
  const detailsRef = useRef(null);
  const [timezoneMoment] = useTimezoneMoment();
  const renderIntlMessage = useRenderIntlMessage();
  const intl = useIntl();
  const classes = useStyles();

  const [detailsHeight, setDetailsHeight] = useState<number>(0);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateParticipantsModal, setShowDuplicateParticipantsModal] = useState(false);
  const [showRemindParticipantsModal, setShowRemindParticipantsModal] = useState(false);
  const [showCancelParticipationModal, setShowCancelParticipationModal] = useState(false);
  const [showConfirmParticipationModal, setShowConfirmParticipationModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);

  const {
    BOOKING_EVENTS_EDIT,
    BOOKING_EVENTS_CANCEL,
    BOOKING_EVENTS_DELETE,
    BOOKING_EVENTS_DUPLICATE,
    BOOKING_EVENTS_REMIND_PARTICIPANTS,
  } = usePermissionContext();

  useEffect(() => {
    const observedRef = detailsRef.current;

    const resizeObserver = new ResizeObserver(entries => {
      const boxHeight = entries[0].contentRect.height;

      const isBlockOverflow = window.innerHeight - 134 < boxHeight;
      if (isBlockOverflow) {
        setDetailsHeight(boxHeight);
      }

      if (handleOverflow) {
        handleOverflow(isBlockOverflow);
      }
    });

    if (observedRef) {
      resizeObserver.observe(observedRef);
    }

    return () => {
      if (observedRef) {
        resizeObserver.unobserve(observedRef);
      }
    };
  });

  const resizeFunction = () => {
    if (window.innerHeight - 134 < detailsHeight) {
      if (handleOverflow) {
        handleOverflow(true);
      }
    } else if (handleOverflow) {
      handleOverflow(false);
    }
  };

  const onSubmitCancelEvent = (formValues: IEventAction) => {
    setShowCancelModal(false);

    if (onCancel) {
      onCancel(formValues);
    }
  };

  const handleDuplicateParticipants = () => {
    if (persons.length) {
      setShowDuplicateParticipantsModal(true);
    } else if (onDuplicate) {
      onDuplicate(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', resizeFunction);
    return () => {
      window.removeEventListener('resize', resizeFunction);
    };
  });

  if (!event) {
    return null;
  }

  const currentDate = timezoneMoment(`${event.date} ${event.time}`, 'YYYY-MM-DD HH:mm', true);
  const persons = getArray<IEventPersonEntity>(event.persons);
  const resources = getArray<IBookingEventResource>(event.resources);
  const endTime = currentDate.clone().add(event.durationInMinutes, 'minutes');
  const trainers: IEventPersonEntity[] = [];
  const participants: IEventPersonEntity[] = [];
  const waitListPersons: IEventPersonEntity[] = [];

  for (const resource of resources) {
    const employee = resource.employee;

    if (employee) {
      trainers.push({
        id: resource.id,
        salesperson: {
          id: Number(employee.id),
          firstName: employee.firstName,
          lastName: employee.lastName,
          imageUrl: employee.imageUrl || undefined,
          active: employee.active,
        },
        eventDate: currentDate.format(),
        joinedDate: '',
      });
    }
  }

  for (const person of persons) {
    if (person.waitList) {
      waitListPersons.push(person);
    } else {
      participants.push(person);
    }
  }

  const isPastEvent = currentDate.isSameOrBefore(timezoneMoment(), 'minutes');
  const canDuplicate = module === PeakModules.Booking;
  const eventDate = moment.utc(`${event.date} ${event.time}`, 'YYYY-MM-DD HH:mm');
  const secondsBeforeEvent = eventDate.diff(moment(), 'seconds');
  const durationInMinutes = event.durationInMinutes;
  const isEventCanceled = !!event.canceled;
  const isAllowSelfBooking = !!event.allowSelfBooking;
  const spotLimited = !!event.spotLimited;
  const overbookingType = event.overbookingType;
  const spotAmount = event.spotAmount ?? 0;
  const bookedSpots = event.bookedSpots ?? 0;
  const editRestrictionBeforeDays = event.editRestrictionBeforeDays;
  const joinRestrictionBeforeDays = event.joinRestrictionBeforeDays;
  const cancelRestrictionBeforeDays = event.cancelRestrictionBeforeDays;
  const allowedParticipantTypes = getArray<EventAttendeeType>(event.allowedParticipantTypes);
  const isWaitList = overbookingType === EventOverbookingType.WAIT_LIST;
  const isWaitListModal = spotLimited && bookedSpots === spotAmount && isWaitList;
  const personsStatusInfo = event.personsStatusInfo;
  let actionsMenu: JSX.Element | null = null;

  if (!readOnly && !isPastEvent) {
    let editAction: JSX.Element | null = null;
    let joinAction: JSX.Element | null = null;
    let confirmParticipationAction: JSX.Element | null = null;
    let cancelParticipationAction: JSX.Element | null = null;
    let duplicateAction: JSX.Element | null = null;
    let remindAction: JSX.Element | null = null;
    let cancelAction: JSX.Element | null = null;
    let restoreAction: JSX.Element | null = null;
    let deleteAction: JSX.Element | null = null;

    if (onEdit && BOOKING_EVENTS_EDIT) {
      editAction = (
        <BookingActionItemEdit
          onClick={() => onEdit(event)}
          secondsBeforeEvent={secondsBeforeEvent}
          editRestrictionBeforeDays={editRestrictionBeforeDays}
        />
      );
    }

    if (onJoinClass && overbookingType) {
      joinAction = (
        <BookingActionItemJoin
          userId={userId}
          isEventCanceled={isEventCanceled}
          isAllowSelfBooking={isAllowSelfBooking}
          spotLimited={spotLimited}
          bookedSpots={bookedSpots}
          spotAmount={spotAmount}
          eventPersons={persons}
          eventParticipantTypes={allowedParticipantTypes}
          allowedAttendeeTypes={[EventAttendeeType.EMPLOYEE, EventAttendeeType.RESOURCE_EMPLOYEE]}
          overbookingType={overbookingType}
          secondsBeforeEvent={secondsBeforeEvent}
          onClick={() => setShowJoinModal(true)}
          joinRestrictionBeforeDays={joinRestrictionBeforeDays}
        />
      );
    }

    if (module !== PeakModules.Booking && appointmentStatus) {
      if (onConfirmParticipation && overbookingType) {
        confirmParticipationAction = (
          <BookingActionItemConfirmParticipation
            onClick={() => setShowConfirmParticipationModal(true)}
            appointmentStatus={appointmentStatus}
            userId={personId || userId}
            eventPersons={persons}
            overbookingType={overbookingType}
            spotLimited={spotLimited}
            bookedSpots={bookedSpots}
            spotAmount={spotAmount}
            isEventCanceled={isEventCanceled}
          />
        );
      }

      if (onCancelParticipation) {
        cancelParticipationAction = (
          <BookingActionItemCancelParticipation
            onClick={() => setShowCancelParticipationModal(true)}
            userId={personId || userId}
            eventPersons={persons}
            appointmentStatus={appointmentStatus}
            isEventCanceled={isEventCanceled}
          />
        );
      }
    }

    if (canDuplicate && BOOKING_EVENTS_DUPLICATE) {
      duplicateAction = (
        <BookingActionItemDuplicate
          onClick={handleDuplicateParticipants}
          isEventCanceled={isEventCanceled}
        />
      );
    }

    if (onRemind && BOOKING_EVENTS_REMIND_PARTICIPANTS) {
      remindAction = (
        <BookingActionItemRemind
          onClick={() => setShowRemindParticipantsModal(true)}
          isEventCanceled={!!event.canceled}
        />
      );
    }

    if (!isEventCanceled && onCancel && BOOKING_EVENTS_CANCEL) {
      cancelAction = (
        <BookingActionItemCancel
          onClick={() => setShowCancelModal(true)}
          secondsBeforeEvent={secondsBeforeEvent}
          cancelRestrictionBeforeDays={cancelRestrictionBeforeDays}
        />
      );
    }

    if (isEventCanceled && onRestore) {
      restoreAction = (
        <BookingActionItemRestore
          onClick={() => onRestore(event)}
          secondsBeforeEvent={secondsBeforeEvent}
        />
      );
    }

    if (onDelete && BOOKING_EVENTS_DELETE) {
      deleteAction = <BookingActionItemDelete onClick={() => setShowDeleteModal(true)} />;
    }

    if (
      editAction ||
      joinAction ||
      confirmParticipationAction ||
      cancelParticipationAction ||
      duplicateAction ||
      remindAction ||
      cancelAction ||
      restoreAction ||
      deleteAction
    ) {
      actionsMenu = (
        <Grid className={classes.row} item>
          <ActionsMenu horizontal>
            {editAction}
            {joinAction}
            {confirmParticipationAction}
            {cancelParticipationAction}
            {duplicateAction}
            {remindAction}
            {cancelAction}
            {restoreAction}
            {deleteAction}
          </ActionsMenu>
        </Grid>
      );
    }
  }

  return (
    <Box className={classes.root}>
      <Box
        className={classes.colorLine}
        style={!readOnly ? { backgroundColor: event.color } : {}}
      />

      <Grid container wrap="nowrap" justifyContent="space-between" className={classes.titleWrapper}>
        <Grid item>
          <Grid container wrap="nowrap" alignItems="flex-end">
            <Typography className={classes.title} variant="h4">
              {event.title}
            </Typography>

            {event.canceled && (
              <Typography component="span" color="textSecondary" className={classes.canceledStatus}>
                {intl.formatMessage(commonMessages.canceled)}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid container wrap="nowrap" className={classes.actionsWrapper}>
          {actionsMenu}

          <Grid item>
            <IconButton className={classes.closeBtn} size="small" onClick={onClose}>
              <SvgIcon className={classes.closeBtnIcon}>
                <Times />
              </SvgIcon>
            </IconButton>
          </Grid>
        </Grid>
      </Grid>

      <Box className={classes.scrollbarWrapper}>
        <ScrollBox hasShadowsOnScroll suppressBottomShadow>
          <div ref={detailsRef} className={classes.detailsWrapper}>
            <Grid container justifyContent="space-between">
              <Grid className={classes.row} item>
                <Event className={classes.rowIcon} />
                <Typography>{currentDate.format('dddd, MMMM D')}</Typography>
              </Grid>

              {event.repeated &&
                event.repeatingFrequency === EventRepeatFrequency.WEEKLY &&
                event.repeatingWeekdays && (
                  <Grid className={classes.row} item>
                    <SvgIcon className={classes.rowIcon}>
                      <Repeat />
                    </SvgIcon>

                    <Typography>{getWeekdaysLabel(event.repeatingWeekdays)}</Typography>
                  </Grid>
                )}
            </Grid>
            <Grid className={classes.row} container>
              <Schedule className={classes.rowIcon} />

              <Typography>{`${currentDate.format('LT')} - ${endTime.format('LT')} (${
                durationInMinutes ? transformMinutesToTimeFormat(durationInMinutes) : ''
              })`}</Typography>
            </Grid>
            {!!event.club && (
              <Grid className={classes.row} container>
                <SvgIcon className={classes.rowIcon}>
                  <Building />
                </SvgIcon>

                <Typography>
                  {event.club?.title.toUpperCase() || event.club?.name?.toUpperCase()}
                </Typography>
              </Grid>
            )}
            {!!resources && !!resources.length ? (
              <Box display="flex" alignItems="center" mt={-0.5} mb={1}>
                <SvgIcon className={classes.rowIcon}>
                  <Package />
                </SvgIcon>

                <Box display="flex" flexWrap="wrap">
                  {resources.map(resourceItem => {
                    const isActiveResource = resourceItem.active;
                    const inactiveTitle = `${resourceItem.title} (${renderIntlMessage(
                      commonMessages.inactiveOption,
                    )})`;

                    return (
                      <Chip
                        key={resourceItem.id}
                        label={isActiveResource ? resourceItem.title : inactiveTitle}
                        className={cx(classes.tag, {
                          [classes.inactive]: !isActiveResource,
                        })}
                      />
                    );
                  })}
                </Box>
              </Box>
            ) : null}
            <Grid className={classes.row} item>
              <SlotsIcon className={classes.rowIcon} />
              <Typography>
                {spotLimited
                  ? intl.formatMessage(messages.slotsTaken, { bookedSpots, spotAmount })
                  : intl.formatMessage(messages.unlimitedSlots)}
              </Typography>
            </Grid>
            <Grid className={classes.participantsRow} item>
              <UserIcon className={classes.rowIcon} />
              <Typography>{`${event.persons?.length} ${intl.formatMessage(
                messages.participantsLabel,
              )}`}</Typography>
            </Grid>
            <Box className={classes.personsStatusInfo}>
              <Typography variant="body2" color="textSecondary">
                {`${personsStatusInfo?.CONFIRMED || 0} ${intl.formatMessage(
                  messages.confirmed,
                )}, ${personsStatusInfo?.ATTENDED || 0} ${intl.formatMessage(
                  messages.attendedLabel,
                )}, ${personsStatusInfo?.MISSED || 0} ${intl.formatMessage(messages.noShowLabel)}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`${personsStatusInfo?.CANCELED || 0} ${intl.formatMessage(
                  messages.canceledLabel,
                )}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`${personsStatusInfo?.PENDING || 0} ${intl.formatMessage(messages.pendingLabel)}`}
              </Typography>
            </Box>
            {!!event.description && (
              <Grid className={classes.row} item>
                <AlignLeftIcon className={classes.rowIcon} />

                <Typography>{event.description}</Typography>
              </Grid>
            )}
            {event.tags?.length ? (
              <Box display="flex" alignItems="center" mt={-0.5} mb={1}>
                <SvgIcon className={classes.rowIcon}>
                  <TagsIcon />
                </SvgIcon>

                <Box display="flex" flexWrap="wrap">
                  {event.tags.map(tagItem => {
                    const isActiveTag = tagItem.active;
                    const inactiveTitle = `${tagItem.title} (${renderIntlMessage(
                      commonMessages.inactiveOption,
                    )})`;

                    return (
                      <Chip
                        key={tagItem.id}
                        label={isActiveTag ? tagItem.title : inactiveTitle}
                        className={cx(classes.tag, {
                          [classes.inactive]: !isActiveTag,
                        })}
                      />
                    );
                  })}
                </Box>
              </Box>
            ) : null}
            {event.service ? (
              <Box display="flex" alignItems="center">
                <ServicesIcon className={classes.rowIcon} />

                <TooltipTypography ellipsized>{event.service.title}</TooltipTypography>
              </Box>
            ) : null}
            {trainers.length ? (
              <ParticipantsList
                title={intl.formatMessage(commonMessages.trainersLabel)}
                data={trainers}
              />
            ) : null}
            {participants.length ? (
              <ParticipantsList
                title={intl.formatMessage(inputLabels.participants)}
                data={participants}
                isParticipants
              />
            ) : null}
            {isWaitList && waitListPersons.length ? (
              <ParticipantsList
                title={intl.formatMessage(messages.overbookingTypeWaitlist)}
                data={waitListPersons}
              />
            ) : null}
            {event.modificationHistory?.length ? (
              <ModificationHistory eventHistory={event.modificationHistory} />
            ) : null}
            {showCancelModal ? (
              <CancelEventModal
                isUpdateEventLoading={isEventActionLoading}
                onSubmit={onSubmitCancelEvent}
                onClose={() => setShowCancelModal(false)}
                type={type}
                onLoadSenderAvailability={onLoadSenderAvailability}
                isRepeatedEvent={event.repeated}
                isOpen
              />
            ) : null}
            {showDeleteModal && onDelete ? (
              <DeleteEventModal
                onSubmit={onDelete}
                onClose={() => setShowDeleteModal(false)}
                isUpdateEventLoading={isEventActionLoading}
                type={type}
                onLoadSenderAvailability={onLoadSenderAvailability}
                isRepeatedEvent={event.repeated}
                isOpen
              />
            ) : null}
            {showDuplicateParticipantsModal && onDuplicate ? (
              <DuplicateParticipantsModal
                isLoading={isEventActionLoading}
                onClose={() => setShowDuplicateParticipantsModal(false)}
                onDuplicate={onDuplicate}
                isOpen
              />
            ) : null}
            {showRemindParticipantsModal && onRemind ? (
              <RemindParticipantsModal
                isLoading={isEventActionLoading}
                onClose={() => setShowRemindParticipantsModal(false)}
                onRemind={onRemind}
                type={SenderAvailabilityTypeList.REMIND}
                module={PeakModules.Booking}
                event={event}
                isOpen
              />
            ) : null}
            {showCancelParticipationModal && onCancelParticipation ? (
              <ParticipationModal
                isUpdateEventLoading={isEventActionLoading}
                dialogTitle={intl.formatMessage(messages.cancelParticipation)}
                isRepeatedEvent={!!event.repeated}
                onSubmit={onCancelParticipation}
                onClose={() => setShowCancelParticipationModal(false)}
                isOpen
              />
            ) : null}
            {showConfirmParticipationModal && onConfirmParticipation ? (
              <ParticipationModal
                isUpdateEventLoading={isEventActionLoading}
                dialogTitle={intl.formatMessage(messages.confirmParticipation)}
                isRepeatedEvent={!!event.repeated}
                onSubmit={onConfirmParticipation}
                onClose={() => setShowConfirmParticipationModal(false)}
                isOpen
              />
            ) : null}
            {showJoinModal && onJoinClass ? (
              <ParticipationModal
                isUpdateEventLoading={isEventActionLoading}
                dialogTitle={
                  isWaitListModal
                    ? intl.formatMessage(messages.joinWaitlist)
                    : intl.formatMessage(messages.joinClass)
                }
                isRepeatedEvent={!!event.repeated}
                onSubmit={onJoinClass}
                onClose={() => setShowJoinModal(false)}
                isWaitlist={isWaitListModal}
                isOpen
              />
            ) : null}
          </div>
        </ScrollBox>
      </Box>
    </Box>
  );
};

export default EventDetails;
